<template>
    <div>
        <div class="complianceModal__title">
            <template v-if="isNumberView"> Number </template>
            <template v-else> Numbers </template>
        </div>

        <template v-if="isAllTollFreeNumbersVerified">
            <form-message
                type="is-warning"
                icon="info-circle"
                :message="'All toll-free numbers verified'"
            />
        </template>

        <div v-if="item && item.numbers.length" class="complianceModal__column">
            <span class="complianceModal__label">Verified</span>
            <span class="complianceModal__numbers">
                <span v-for="currentNumber in item.numbers" class="complianceModal__number">
                    {{ currentNumber.formatted_number }}
                </span>
            </span>
        </div>
        <div v-if="isNumberView" class="complianceModal__column">
            <span class="complianceModal__numbers">
                <span class="complianceModal__number">{{ phoneUpdateData.formatted_number }}</span>
            </span>
        </div>
        <div v-if="!isNumberView" class="complianceModal__column">
            <form-dropdown
                id="dropdown-numbers"
                ref="dropdownNumbers"
                label="Toll-Free numbers"
                :placeholder="'Select'"
                :settings="dropdownSettingsNumbers"
                :hideLabelColon="true"
                :hideRequired="true"
                :disabled="form.busy || disabledNumbersStep"
                @input="updateNumbers"
            />
        </div>
        <template v-if="isShowExplanation">
            <div class="complianceModal__column">
                <form-textarea
                    v-model="form.explanation_description"
                    label="Explanation"
                    subLabel="Carriers need to know the reason you want to use multiple toll-free numbers under 1 use case"
                    placeholder="Enter explanation"
                    :error="form.errors.get('explanation_description')"
                    :hideLabelColon="true"
                    :disabled="form.busy"
                    :maxlength="500"
                />
            </div>
        </template>
    </div>
</template>

<script>
import { mapState } from 'pinia';
import stores from 'store/modules';

export default {
    props: {
        item: { type: Object, default: null },
        form: { type: Object, default: null },
        number: { type: Object, default: null },
        phoneUpdateData: { type: Object, default: null },
        isNumberView: { type: Boolean, default: null },
        getAvailableNumbers: { type: Array, default: () => [] },
        disabledNumbersStep: { type: Boolean, default: false },
        isShowExplanation: { type: Boolean, default: false },
        isAllItemStepsCompleted: { type: Boolean, default: false },
        isAllTollFreeNumbersVerified: { type: Boolean, default: false },
    },

    emits: [],

    data: () => ({
        showSelectAll: false,
    }),

    computed: {
        dropdownSettingsNumbers() {
            return {
                valueField: 'id',
                searchField: ['formattedNumber'],
                options: this.options,
                persist: false,
                highlight: true,
                dropdownClass: 'selectize-dropdown selectize-dropdown-column',
                render: {
                    item: this.dropdownNumberItem,
                    option: this.dropdownNumberOption,
                },
                onChangeNewField: () => {
                    this.setAllNumber();
                },
                newFieldFirst: true,
                maxItems: null,
                dropdownParent: 'body',
                plugins: ['remove_button'],
            };
        },

        optionAll() {
            return {
                id: 'new_field',
                label: 'Select numbers',
                button: 'Select all',
                group: 'main',
                $order: -1,
            };
        },

        options() {
            const optionSelectAll = this.optionAll;
            const options = this.getAvailableNumbers.map((item) => ({
                id: item?.id,
                number: item.formatted_number,
                inboxName: item.inbox.name,
                group: 'main',
            }));

            if (this.getAvailableNumbers.length === 1) {
                return options;
            }

            return [optionSelectAll, ...options];
        },

        ...mapState(stores.Number, ['numbers', 'unassignedNumbers']),
    },

    watch: {
        'form.numbers.length'() {
            this.showSelectAll = this.getAvailableNumbers?.length !== this.form.numbers?.length;
        },

        showSelectAll(value) {
            if (!this.$refs.dropdownNumbers) return;

            if (value) {
                this.$refs.dropdownNumbers.dropdown.addOption(this.optionAll);
            } else {
                this.$refs.dropdownNumbers.dropdown.removeOption(this.optionAll.id);
                this.$refs.dropdownNumbers.dropdown.refreshOptions();
            }
        },
    },

    methods: {
        init() {
            this.setIsOneNumber();

            if (this.getAvailableNumbers?.length > 1) {
                this.showSelectAll = true;
            }
        },

        reset() {
            this.showSelectAll = false;
        },

        setIsOneNumber() {
            if (!this.getAvailableNumbers?.length) return;
            if (this.disabledNumbersStep) {
                this.setAllNumber();

                return;
            }

            if (this.getAvailableNumbers?.length === 1) {
                this.setFirstNumber();
            }
        },

        async setFirstNumber() {
            if (!this.getAvailableNumbers?.length) return;

            if (!this.form.numbers.includes(this.getAvailableNumbers[0].id)) {
                this.form.numbers.push(this.getAvailableNumbers[0].id);
            }

            await this.$delay(50);

            if (this.$refs.dropdownNumbers) {
                this.$refs.dropdownNumbers.dropdown.addItems(this.getAvailableNumbers[0].id);
            }
        },

        async setAllNumber() {
            if (!this.getAvailableNumbers?.length) return;

            const ids = this.getAvailableNumbers.map((item) => item.id);

            this.form.numbers = ids;

            await this.$delay(50);

            if (this.$refs.dropdownNumbers) {
                this.$refs.dropdownNumbers.dropdown.addItems(ids);
            }
        },

        updateNumbers(data) {
            this.form.numbers = data.filter((item) => item !== 'all').map((item) => Number(item));
        },

        async handleSelectAllNumbers(e) {
            e.stopPropagation();
            e.preventDefault();

            const ids = this.getAvailableNumbers.map((item) => item.id);

            await this.$delay(80);

            if (this.$refs.dropdownNumbers) {
                this.$refs.dropdownNumbers.dropdown.addItems(ids);
            }

            this.showSelectAll = false;
        },

        dropdownNumberItem(item, escape) {
            return `<div class='selectize-contact tag is-small'>
                        <span>${escape(item.number)}</span>
                    </div>`;
        },

        dropdownNumberOption(item, escape) {
            if (item.id === 'new_field') {
                return `
                    <div class="selectize-option__columns dropdownTools">
                        <div class="label">
                            ${escape(item.label)}
                        </div>
                        <span class="button">
                            ${escape(item.button)}
                        </span>
                    </div>
                `;
            }

            return `<div class='selectize-option__columns'>
                        <div class='selectize-option__column'>
                            <span>
                                ${escape(item.number)}
                            </span>
                        </div>
                    </div>`;
        },
    },

    mounted() {
        this.init();
    },

    beforeUnmount() {
        this.reset();
    },
};
</script>

<style lang="scss">
@import 'styles.module.scss';
</style>
